import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import UploadCard from "../../components/commons/UploadCard";
import BannerCarousel from "../../components/partials/banner/BannerCarousel";
import BannerTextSection from "../../components/partials/banner/BannerTextFields";
import Button from "../../components/commons/Button";
import type { Banner } from "../../services/webServices/banner/types";
import { getBanner } from "../../services/webServices/banner/get";
import { postBanner } from "../../services/webServices/banner/post";
import { patchBanner } from "../../services/webServices/banner/patch";
import { deleteBanner } from "../../services/webServices/banner/delete";
import { useAdminLoading } from "../../context/AdminLoadingContext";
import { useInitialPageLoad } from "../../components/layouts/admin-layout/useInitialPageLoad";

export default function Banner() {

  const [banner, setBanner] = useState<Banner[]>([]);
  const [editingBanner, setEditingBanner] = useState<Banner | null>(null);

  const [desktopCoverFile, setDesktopCoverFile] = useState<File | null>(null);
  const [phoneCoverFile, setPhoneCoverFile] = useState<File | null>(null);

  const [title, setTitle] = useState("");
  const [relatedContent, setRelatedContent] = useState("");
  const [description, setDescription] = useState("");

  const [formKey, setFormKey] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const { showLoading, hideLoading } = useAdminLoading();
  const isFirstLoad = useInitialPageLoad();

  const fetchBanner = async () => {

    if (isFirstLoad) showLoading();
    setIsLoading(true);

    const res = await getBanner();
    setBanner(res ?? []);

    setIsLoading(false);
    if (isFirstLoad) hideLoading();
  };

  useEffect(() => {
    fetchBanner();
  }, []);

  const resetForm = () => {
    setEditingBanner(null);
    setDesktopCoverFile(null);
    setPhoneCoverFile(null);
    setTitle("");
    setRelatedContent("");
    setDescription("");
    setFormKey(prev => prev + 1);
  };

  const handleEdit = (banner: Banner) => {
    setEditingBanner(banner);
    setTitle(banner.title);
    setRelatedContent(banner.related_content);
    setDescription(banner.description);
    setDesktopCoverFile(null);
    setPhoneCoverFile(null);
    setFormKey(prev => prev + 1);
  };

  const handleDelete = async (uuid: string) => {
    const success = await deleteBanner({ uuid });

    if (!success) return;

    toast.success("بنر با موفقیت حذف شد");
    if (editingBanner?.uuid === uuid) resetForm();
    fetchBanner();
  };

  const handleSubmit = async () => {
    if (
      !editingBanner &&
      (!desktopCoverFile || !phoneCoverFile || !title || !description || !relatedContent)
    ) {
      toast.info("لطفا همه فیلدها را کامل کنید");
      return;
    }

    setIsLoading(true);

    if (!editingBanner) {
      const success = await postBanner({
        desktopCoverFile: desktopCoverFile!,
        phoneCoverFile: phoneCoverFile!,
        title: title,
        related_content: relatedContent,
        description: description,
      });

      if (!success) return;

      toast.success("بنر با موفقیت ثبت شد");
    } else {
      const success = await patchBanner({
        uuid: editingBanner.uuid,
        originalBanner: editingBanner,
        desktopCoverFile: desktopCoverFile!,
        phoneCoverFile: phoneCoverFile!,
        title: title,
        related_content: relatedContent,
        description: description,
      });

      if (!success) return;

      toast.success("بنر با موفقیت ویرایش شد");
    }

    resetForm();
    fetchBanner();
    setIsLoading(false);
  };

  return (
    <div className="flex flex-col gap-4">
      <BannerCarousel
        banner={banner}
        isLoading={isLoading}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      <div className="flex flex-row gap-4">
        <UploadCard
          key={`desktop-cover-${formKey}`}
          title="بنر مخصوص دسکتاپ"
          description="این تصویر به‌عنوان بنر در صفحه اصلی نمایش داده می‌شود."
          helperText="تنها فرمت‌های JPG و PNG پشتیبانی می‌شوند"
          mode="image"
          cropAspect={1512 / 314}
          initialValue={
            editingBanner
              ? {
                  type: "image",
                  previewUrl:
                    process.env.REACT_APP_File_URL +
                    editingBanner.desktop_cover_image,
                }
              : undefined
          }
          onChange={setDesktopCoverFile}
        />

        <UploadCard
          key={`phone-cover-${formKey}`}
          title="بنر مخصوص موبایل"
          description="این تصویر به‌عنوان بنر در صفحه اصلی نمایش داده می‌شود."
          helperText="تنها فرمت‌های JPG و PNG پشتیبانی می‌شوند"
          mode="image"
          cropAspect={1080 / 441}
          initialValue={
            editingBanner
              ? {
                  type: "image",
                  previewUrl:
                    process.env.REACT_APP_File_URL +
                    editingBanner.phone_cover_image,
                }
              : undefined
          }
          onChange={setPhoneCoverFile}
        />
      </div>

      <BannerTextSection
        title={title}
        relatedContent={relatedContent}
        description={description}
        onTitleChange={setTitle}
        onRelatedContentChange={setRelatedContent}
        onDescriptionChange={setDescription}
      />

      <div className="flex flex-row justify-end gap-4">
        <Button
          variant="gray"
          style="solid"
          size="lg"
          text="انصراف"
          loading={isLoading}
          disabled={false}
          onClick={resetForm}
        />

        <Button
          variant="primary"
          style="solid"
          size="lg"
          text={editingBanner ? "ویرایش" : "ارسال"}
          loading={isLoading}
          disabled={false}
          onClick={handleSubmit}
        />
      </div>
    </div>
  );
};